import { useMemo, useState } from "react";
import { Link } from "wouter";
import { customFetch } from "@workspace/api-client-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parse,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";

type Slot = { start: string; end: string };
type AvailabilityWindow = {
  id: string;
  date: string;
  start: string;
  end: string;
  slotMinutes: number;
  slots: Slot[];
};

const DURATIONS = [15, 20, 30, 40, 45, 50, 60, 90, 120];
const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

// format() usa o relógio local; toISOString() viraria o dia depois das 21h.
const dayKey = (d: Date) => format(d, "yyyy-MM-dd");

// Só a primeira letra: o `capitalize` do CSS faria "Outubro De 2026".
const upperFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const toMinutes = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const fromMinutes = (n: number) =>
  `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;

// Mesma conta do servidor: meets inteiros a partir do início, sobra no fim descartada.
function previewSlots(start: string, end: string, minutes: number): string[] {
  if (!start || !end) return [];
  const out: string[] = [];
  for (let t = toMinutes(start); t + minutes <= toMinutes(end); t += minutes) out.push(fromMinutes(t));
  return out;
}

export default function Closer() {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(() => dayKey(new Date()));
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("12:00");
  const [minutes, setMinutes] = useState(45);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const gridDays = useMemo(
    () => eachDayOfInterval({ start: startOfWeek(startOfMonth(month)), end: endOfWeek(endOfMonth(month)) }),
    [month],
  );
  const from = dayKey(gridDays[0]);
  const to = dayKey(gridDays[gridDays.length - 1]);

  const { data, isLoading } = useQuery({
    queryKey: ["closer-availability", from, to],
    queryFn: () =>
      customFetch<{ windows: AvailabilityWindow[] }>(`/api/admin/closer/availability?from=${from}&to=${to}`),
  });

  const byDay = useMemo(() => {
    const map = new Map<string, AvailabilityWindow[]>();
    for (const w of data?.windows ?? []) map.set(w.date, [...(map.get(w.date) ?? []), w]);
    return map;
  }, [data]);

  const preview = previewSlots(start, end, minutes);
  const selectedDate = parse(selected, "yyyy-MM-dd", new Date());
  const selectedWindows = byDay.get(selected) ?? [];

  const create = useMutation({
    mutationFn: () =>
      customFetch<AvailabilityWindow>("/api/admin/closer/availability", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selected, start, end, slotMinutes: minutes }),
      }),
    onSuccess: (w) => {
      queryClient.invalidateQueries({ queryKey: ["closer-availability"] });
      toast({ title: `${w.slots.length} ${w.slots.length === 1 ? "meet adicionado" : "meets adicionados"}` });
    },
    onError: (err) =>
      toast({ title: "Não deu para adicionar", description: extractApiErrorMessage(err), variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => customFetch(`/api/admin/closer/availability/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["closer-availability"] }),
    onError: (err) =>
      toast({ title: "Não deu para apagar", description: extractApiErrorMessage(err), variant: "destructive" }),
  });

  const pickDay = (key: string) => {
    if (!key) return;
    setSelected(key);
    const d = parse(key, "yyyy-MM-dd", new Date());
    if (!isSameMonth(d, month)) setMonth(startOfMonth(d));
  };

  const askRemove = async (w: AvailabilityWindow) => {
    const ok = await confirm({
      title: "Apagar disponibilidade?",
      description: `${w.start} às ${w.end} · ${w.slots.length} meets de ${w.slotMinutes} min.`,
      confirmLabel: "Apagar",
      destructive: true,
    });
    if (ok) remove.mutate(w.id);
  };

  return (
    <div className="space-y-6 px-4 py-6 sm:p-[30px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display">Closer</h1>
          <p className="text-muted-foreground text-sm mt-1">Horários livres para meet com leads</p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link href="/results">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Resultados
          </Link>
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">{upperFirst(format(month, "MMMM 'de' yyyy", { locale: ptBR }))}</CardTitle>
            <div className="flex items-center gap-1">
              {isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mr-2" />}
              <Button variant="ghost" size="icon" aria-label="Mês anterior" onClick={() => setMonth((m) => subMonths(m, 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Próximo mês" onClick={() => setMonth((m) => addMonths(m, 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground mb-1">
              {WEEKDAYS.map((d, i) => (
                <div key={i}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {gridDays.map((d) => {
                const key = dayKey(d);
                const count = (byDay.get(key) ?? []).reduce((n, w) => n + w.slots.length, 0);
                const isSelected = key === selected;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => pickDay(key)}
                    className={[
                      "min-h-[56px] sm:min-h-[72px] rounded-lg border p-1.5 text-left transition-colors flex flex-col",
                      isSelected ? "border-primary bg-primary/10" : "hover:bg-muted",
                      isSameMonth(d, month) ? "" : "opacity-40",
                    ].join(" ")}
                  >
                    <span className={`text-sm ${isToday(d) ? "font-bold text-primary" : ""}`}>{format(d, "d")}</span>
                    {count > 0 && (
                      <span className="mt-auto text-[11px] leading-tight font-medium text-primary">
                        {count} {count === 1 ? "meet" : "meets"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Adicionar disponibilidade</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="closer-day">Dia</Label>
                <DataBr id="closer-day" value={selected} onChange={pickDay} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="closer-start">Início</Label>
                  <Input id="closer-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="closer-end">Fim</Label>
                  <Input id="closer-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Duração de cada meet</Label>
                <Select value={String(minutes)} onValueChange={(v) => setMinutes(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {m} min
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-muted-foreground">
                {preview.length > 0
                  ? `${preview.length} ${preview.length === 1 ? "meet" : "meets"}: ${preview.join(" · ")}`
                  : "Nenhum meet cabe nesse intervalo."}
              </p>
              <Button
                className="w-full"
                disabled={preview.length === 0 || create.isPending}
                onClick={() => create.mutate()}
              >
                {create.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Adicionar
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {upperFirst(format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR }))}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedWindows.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhuma disponibilidade neste dia.</p>
              )}
              {selectedWindows.map((w) => (
                <div key={w.id} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">
                      {w.start} às {w.end} · {w.slotMinutes} min
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Apagar disponibilidade"
                      disabled={remove.isPending}
                      onClick={() => askRemove(w)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {w.slots.map((s) => (
                      <span key={s.start} className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {s.start}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
